/**
 * CreateTicketDialog component
 *
 * A shadcn/ui Dialog for creating a Jira ticket linked to a security issue.
 * Auto-generates a summary from the issue data (editable by the user).
 * Shows a success state with the ticket URL as a clickable link.
 *
 * GEC-25: Pre-checks whether Jira is configured before showing the form.
 * Fetches projects and issue types dynamically from Jira.
 */

import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { ExternalLink, Loader2, AlertTriangle, Settings } from "lucide-react";
import { useCreateIssueTicket } from "@/hooks/useIssueTickets";
import { useJiraIntegration } from "@/hooks/useJiraIntegration";
import {
  getJiraProjects,
  getJiraIssueTypes,
} from "@/services/jiraIntegrationService";
import type { SecurityIssue } from "@/types/security";
import type { IssueTicket } from "@/types/issueTicket";

interface CreateTicketDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  issueId: number;
  issue: SecurityIssue;
}

export function CreateTicketDialog({
  open,
  onOpenChange,
  issueId,
  issue,
}: CreateTicketDialogProps) {
  const createTicketMutation = useCreateIssueTicket();
  const { data: jiraIntegration, isLoading: isLoadingJira } = useJiraIntegration();

  const [projectKey, setProjectKey] = useState("");
  const [summary, setSummary] = useState("");
  const [issueType, setIssueType] = useState("");
  const [createdTicket, setCreatedTicket] = useState<IssueTicket | null>(null);

  const isJiraConfigured = jiraIntegration != null;

  // Fetch Jira projects when dialog is open and Jira is configured
  const { data: projects, isLoading: isLoadingProjects } = useQuery({
    queryKey: ["jira-projects"],
    queryFn: getJiraProjects,
    enabled: open && isJiraConfigured,
    staleTime: 60_000,
    retry: false,
  });

  // Fetch issue types when a project is selected
  const { data: issueTypes, isLoading: isLoadingTypes } = useQuery({
    queryKey: ["jira-issue-types", projectKey],
    queryFn: () => getJiraIssueTypes(projectKey),
    enabled: projectKey.length >= 2,
    staleTime: 60_000,
    retry: false,
  });

  // Auto-select first issue type when types are fetched
  useEffect(() => {
    if (issueTypes && issueTypes.length > 0) {
      const taskType = issueTypes.find((t) => t.name === "Task");
      setIssueType(taskType ? taskType.name : issueTypes[0].name);
    } else {
      setIssueType("");
    }
  }, [issueTypes]);

  // Auto-generate summary and pre-fill project key when dialog opens
  useEffect(() => {
    if (open) {
      setSummary(`[${issue.severity}] ${issue.title}`);
      setCreatedTicket(null);
      if (jiraIntegration?.default_project_key) {
        setProjectKey(jiraIntegration.default_project_key);
      }
    }
  }, [open, issue.severity, issue.title, jiraIntegration?.default_project_key]);

  // When projects load and no project is selected yet, auto-select
  // the default from integration or the first project
  useEffect(() => {
    if (!projects || projects.length === 0) return;
    if (projectKey) return; // already selected
    if (jiraIntegration?.default_project_key) {
      setProjectKey(jiraIntegration.default_project_key);
    } else {
      setProjectKey(projects[0].key);
    }
  }, [projects, projectKey, jiraIntegration?.default_project_key]);

  const handleProjectChange = (key: string) => {
    setProjectKey(key);
    setIssueType(""); // reset issue type when project changes
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    if (!projectKey.trim() || !issueType) return;

    createTicketMutation.mutate(
      {
        issueId,
        data: {
          provider: "jira",
          project_key: projectKey.trim().toUpperCase(),
          issue_type: issueType,
          summary: summary.trim() || undefined,
        },
      },
      {
        onSuccess: (ticket) => {
          setCreatedTicket(ticket);
        },
      }
    );
  };

  const handleClose = () => {
    setProjectKey("");
    setSummary("");
    setIssueType("");
    setCreatedTicket(null);
    onOpenChange(false);
  };

  const isJiraActive = jiraIntegration?.is_active === true;

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-lg max-h-[85vh] flex flex-col">
        <DialogHeader className="flex-shrink-0">
          <DialogTitle>Create Jira Ticket</DialogTitle>
          <DialogDescription>
            Create a Jira ticket linked to this security issue.
          </DialogDescription>
        </DialogHeader>

        <div className="overflow-y-auto flex-1 min-h-0 p-1">
        {/* Loading state */}
        {isLoadingJira ? (
          <div className="space-y-3 py-2">
            <Skeleton className="h-4 w-48" />
            <Skeleton className="h-10 w-full" />
            <Skeleton className="h-10 w-full" />
          </div>
        ) : !isJiraConfigured ? (
          /* GEC-25: Jira not configured */
          <div className="space-y-4 py-2">
            <div className="flex items-start gap-3 rounded-lg border border-yellow-200 bg-yellow-50 p-4 text-sm text-yellow-800 dark:border-yellow-800 dark:bg-yellow-950 dark:text-yellow-200">
              <AlertTriangle className="h-5 w-5 mt-0.5 flex-shrink-0" />
              <div>
                <p className="font-medium">Jira is not configured for your organization.</p>
                <p className="text-xs mt-1">
                  Go to Settings &gt; Integrations to set it up.
                </p>
              </div>
            </div>
            <Link to="/settings?section=integrations" onClick={handleClose}>
              <Button variant="outline" className="w-full gap-2">
                <Settings className="h-4 w-4" />
                Go to Integration Settings
              </Button>
            </Link>
          </div>
        ) : !isJiraActive ? (
          /* Jira configured but inactive */
          <div className="space-y-4 py-2">
            <div className="flex items-start gap-3 rounded-lg border border-yellow-200 bg-yellow-50 p-4 text-sm text-yellow-800 dark:border-yellow-800 dark:bg-yellow-950 dark:text-yellow-200">
              <AlertTriangle className="h-5 w-5 mt-0.5 flex-shrink-0" />
              <div>
                <p className="font-medium">Jira integration is currently inactive.</p>
                <p className="text-xs mt-1">
                  The integration may have been disabled or encountered errors.
                  Check Settings &gt; Integrations for details.
                </p>
              </div>
            </div>
            <Link to="/settings?section=integrations" onClick={handleClose}>
              <Button variant="outline" className="w-full gap-2">
                <Settings className="h-4 w-4" />
                Go to Integration Settings
              </Button>
            </Link>
          </div>
        ) : createdTicket ? (
          /* Success state */
          <div className="space-y-4">
            <div className="p-4 bg-green-50 rounded-lg border border-green-200">
              <div className="text-sm font-medium text-green-800 mb-2">
                Ticket created successfully
              </div>
              <a
                href={createdTicket.external_ticket_url}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1.5 text-sm font-medium text-blue-600 hover:text-blue-800 underline"
              >
                {createdTicket.external_ticket_id}
                <ExternalLink className="h-3.5 w-3.5" />
              </a>
              <p className="text-xs text-muted-foreground mt-1">
                {createdTicket.summary}
              </p>
            </div>
            <Button variant="outline" onClick={handleClose} className="w-full">
              Close
            </Button>
          </div>
        ) : (
          /* Form state */
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="project-key">Project</Label>
              {isLoadingProjects ? (
                <Skeleton className="h-10 w-full" />
              ) : projects && projects.length > 0 ? (
                <Select
                  value={projectKey}
                  onValueChange={handleProjectChange}
                  disabled={createTicketMutation.isPending}
                >
                  <SelectTrigger id="project-key">
                    <SelectValue placeholder="Select a project" />
                  </SelectTrigger>
                  <SelectContent>
                    {projects.map((p) => (
                      <SelectItem key={p.key} value={p.key}>
                        {p.key} — {p.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              ) : (
                <Input
                  id="project-key"
                  placeholder="e.g. SEC"
                  value={projectKey}
                  onChange={(e) => handleProjectChange(e.target.value.toUpperCase())}
                  disabled={createTicketMutation.isPending}
                  required
                />
              )}
            </div>

            <div className="space-y-2">
              <Label htmlFor="issue-type">Issue Type</Label>
              {isLoadingTypes ? (
                <Skeleton className="h-10 w-full" />
              ) : issueTypes && issueTypes.length > 0 ? (
                <Select
                  value={issueType}
                  onValueChange={setIssueType}
                  disabled={createTicketMutation.isPending}
                >
                  <SelectTrigger id="issue-type">
                    <SelectValue placeholder="Select issue type" />
                  </SelectTrigger>
                  <SelectContent>
                    {issueTypes.map((t) => (
                      <SelectItem key={t.id} value={t.name}>
                        {t.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              ) : (
                <p className="text-xs text-muted-foreground py-2">
                  Select a project to load available issue types.
                </p>
              )}
            </div>

            <div className="space-y-2">
              <Label htmlFor="summary">Summary</Label>
              <Input
                id="summary"
                value={summary}
                onChange={(e) => setSummary(e.target.value)}
                disabled={createTicketMutation.isPending}
                maxLength={255}
              />
              <p className="text-xs text-muted-foreground">
                Auto-generated from the issue. You can edit it before creating.
              </p>
            </div>

            <Button
              type="submit"
              disabled={!projectKey.trim() || !issueType || createTicketMutation.isPending}
              className="w-full"
            >
              {createTicketMutation.isPending ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Creating ticket...
                </>
              ) : (
                "Create Ticket"
              )}
            </Button>
          </form>
        )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
